import { Link, type To } from 'react-router-dom';
import { Loader2, type LucideIcon } from 'lucide-react';

/**
 * The call to action inside a gate (LockedCard, DemoBanner): a router link when
 * it has `to`, otherwise a button. Same paint and focus ring as base/Button, so a
 * gate reads as part of the design system; it exists because Button renders a
 * <button>, and wrapping one in a <Link> nests two interactive elements.
 *
 * Proportion rule (THEME): label 0.875rem with a 1rem icon (sm) or 0.9375rem
 * with a 1.125rem icon (md); every target is at least 2.75rem tall.
 */
export interface GateActionProps {
  label: string;
  /** Internal route. Renders a router <Link>. */
  to?: To;
  /** Router state for `to`, e.g. `{ from: location }` for /signin. */
  state?: unknown;
  /** In-page action. Renders a <button> when there is no `to`. */
  onClick?: () => void;
  icon?: LucideIcon;
  /** Flip a directional icon (an arrow) in right-to-left layouts. */
  mirrorIconInRtl?: boolean;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md';
  /** Buttons only: show a spinner and refuse clicks while work is running. */
  busy?: boolean;
  className?: string;
}

const BASE =
  'inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl font-bold transition-colors duration-200 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#004D40] ' +
  'dark:focus-visible:ring-[#7FD6C3] dark:focus-visible:ring-offset-[var(--pv-color-surface-strong)] ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

const VARIANTS: Record<NonNullable<GateActionProps['variant']>, string> = {
  primary:
    'bg-[#004D40] text-white shadow-brand-xs hover:bg-[#003930] active:bg-[#002A23] ' +
    'dark:bg-[#3FBBA2] dark:text-[#002A23] dark:hover:bg-[#7FD6C3]',
  secondary:
    'border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] text-[var(--pv-color-primary)] ' +
    'hover:border-[var(--pv-color-accent-soft)] hover:bg-[var(--pv-color-surface-sunken)]',
};

const SIZES: Record<NonNullable<GateActionProps['size']>, { box: string; icon: string }> = {
  sm: { box: 'px-4 text-sm', icon: 'h-4 w-4' },
  md: { box: 'px-5 text-[0.9375rem]', icon: 'h-[1.125rem] w-[1.125rem]' },
};

export default function GateAction({
  label,
  to,
  state,
  onClick,
  icon: Icon,
  mirrorIconInRtl = false,
  variant = 'primary',
  size = 'md',
  busy = false,
  className = '',
}: GateActionProps) {
  const sizing = SIZES[size];
  const classes = `${BASE} ${VARIANTS[variant]} ${sizing.box} ${className}`.trim();
  const iconClass = `${sizing.icon} shrink-0${mirrorIconInRtl ? ' rtl:-scale-x-100' : ''}`;

  if (to !== undefined) {
    return (
      <Link to={to} state={state} onClick={onClick} className={classes}>
        {Icon ? <Icon className={iconClass} aria-hidden="true" /> : null}
        <span>{label}</span>
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} disabled={busy} aria-busy={busy || undefined} className={classes}>
      {busy ? (
        <Loader2 className={`${sizing.icon} shrink-0 animate-spin motion-reduce:animate-none`} aria-hidden="true" />
      ) : Icon ? (
        <Icon className={iconClass} aria-hidden="true" />
      ) : null}
      <span>{label}</span>
    </button>
  );
}
